<template>
  <DashboardCard
    :heading="translateUnpaidRent"
    iconClass="budicon-alert-sign"
    :loading="isLoading"
    :showMessage="showError"
    :message="errorMessage"
  >
    <template v-if="hasUnpaidRent">
      <div v-if="isProfessionalLandlord" class="myPageUnpaidRent__proData">
        <ReportsDashboardMetric :value="paymentDeviationTotalCount" :legend="translateOverdueInvoices" small />
        <ReportsDashboardMetric
          :value="paymentDeviationTotalMoney"
          format="currency"
          :legend="translateOverdueRent"
          small
        />
      </div>

      <PaymentsList v-else :data="latestOverdueInvoicesTableData" :columns="paymentsColumns" />

      <div class="myPageUnpaidRent__overviewWrapper mypage-cta text-center">
        <router-link :to="unpaidRentOverviewRoute" class="btn btn-sm btn-outline-secondary">
          {{ translateToAnOverview }}
          <i class="budicon budicon-arrow-right"></i>
        </router-link>
      </div>
    </template>

    <p v-else>{{ translateNoUnpaid }}</p>
  </DashboardCard>
</template>

<script>
import DashboardCard from '@/components/DashboardCard';
import PaymentsList from '@/components/PaymentsList';
import ReportsDashboardMetric from '@/components/reports/dashboard/ReportsDashboardMetric';
import i18nCommon from '@modules/i18n/mixins/Common';
import { mapActions, mapGetters, mapState } from 'vuex';

export default {
  components: {
    ReportsDashboardMetric,
    DashboardCard,
    PaymentsList,
  },
  mixins: [i18nCommon],
  props: {
    errorMessage: {
      type: String,
    },
    showError: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState({
      isLoadingMetrics: state => state.reports.dashboard.loading,
      isLoadingDueRent: state => state.reports.dueRent.loading,
    }),
    ...mapGetters({
      landlordProfiles: 'getLandlordProfilesMeta',
      condensedRentalObjects: 'getCondensedRentalObjects',
    }),
    ...mapGetters('reports', {
      paymentDeviation: 'getDashboardPaymentDeviationTotals',
      latestOverdueInvoices: 'getLatestOverdueInvoices',
    }),
    isLoading() {
      return this.isLoadingMetrics || this.isLoadingDueRent;
    },
    paymentDeviationTotalCount() {
      return (
        this.paymentDeviation.count.from1To15 +
        this.paymentDeviation.count.from16To30 +
        this.paymentDeviation.count.from30
      );
    },
    paymentDeviationTotalMoney() {
      return this.paymentDeviation.money.due;
    },
    isProfessionalLandlord() {
      return this.landlordProfiles.some(ll => ll.meta.isProfessional);
    },
    unpaidRentOverviewRoute() {
      if (this.latestOverdueInvoices.length === 1) {
        const invoice = this.latestOverdueInvoices[0];

        return {
          name: 'tenancy_payments',
          params: {
            landlordId: invoice.landlordId,
            rentalObjectId: invoice.rentalObjectId,
            tenancyId: invoice.tenancyId,
          },
        };
      }

      if (this.singleRentalObjectUser)
        return {
          name: 'rentalObject_tenancies',
          params: {
            landlordId: this.landlordProfiles[0].id,
            rentalObjectId: this.condensedRentalObjects[0].id,
          },
        };

      if (this.landlordProfiles.length === 1)
        return { name: 'homeProd', params: { landlordId: this.landlordProfiles[0].id } };

      return { name: 'reports_customer_list' };
    },
    hasUnpaidRent() {
      return this.isProfessionalLandlord ? this.paymentDeviationTotalCount > 0 : this.latestOverdueInvoices.length;
    },
    latestOverdueInvoicesTableData() {
      return this.latestOverdueInvoices.slice(0, 3).map(invoice => ({
        id: invoice.invoiceId,
        period: invoice.invoiceRentPeriod,
        tenant: invoice.tenantFullname,
        amount: this.toCurrency('NOK', invoice.invoiceBalance),
        status: 'FAIL',
      }));
    },
    paymentsColumns() {
      return [
        {
          title: this.translateInvoicePeriod,
          dataKey: 'period',
        },
        {
          title: this.translateInvoiceTenant,
          dataKey: 'tenant',
        },
        {
          title: this.translateInvoiceAmount,
          dataKey: 'amount',
        },
      ];
    },
    translateUnpaidRent() {
      return this.translate('unpaidRentCard.Unpaid rent');
    },
    translateToAnOverview() {
      return this.translate('unpaidRentCard.To an overview');
    },
    translateOverdueInvoices() {
      return this.translate('unpaidRentCard.Overdue invoices');
    },
    translateOverdueRent() {
      return this.translate('unpaidRentCard.Overdue rent');
    },
    translateInvoicePeriod() {
      return this.translate('unpaidRentCard.invoice.Period');
    },
    translateInvoiceTenant() {
      return this.translate('unpaidRentCard.invoice.Tenant');
    },
    translateInvoiceAmount() {
      return this.translate('unpaidRentCard.invoice.Amount');
    },
    translateNoUnpaid() {
      return this.translate('unpaidRentCard.You do not have unpaid rents');
    },
  },
  watch: {
    isLoadingMetrics: {
      handler(value) {
        if (value || this.isProfessionalLandlord) return;

        this.fetchDueRent({
          landlordIds: this.landlordProfiles.map(ll => ll.id),
        });
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions('reports', ['fetchDueRent']),
  },
};
</script>

<style>
.myPageUnpaidRent__proData {
  @apply mt-4 grid grid-cols-2;
}

.myPageUnpaidRent__overviewWrapper {
  @apply mt-4;
}
</style>
